import React, { createContext, useContext, useState, ReactNode } from 'react';
import routes from '@commons/routes';
import { useNavigate } from 'react-router';

interface Client {
  expiresIn: number;
  folder: {
    createdAt: string;
    folderName: string;
    owner: string;
    fileCount: number;
  };
  token: string;
}

interface ClientContextType {
  client: Client | null;
  clientToken: string;
  login: (client: Client) => void;
  logout: () => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(() => {
    const clientJson = localStorage.getItem('client');
    return clientJson ? JSON.parse(clientJson) : null;
  });

  const [clientToken, setClientToken] = useState<string>(() => {
    return localStorage.getItem('clientToken') || '';
  });

  const login = (client: Client) => {
    setClient(client);
    localStorage.setItem('client', JSON.stringify(client));

    setClientToken(client.token);
    localStorage.setItem('clientToken', client.token);

    navigate(routes.routes.clientFolder(client.folder.folderName));
  };

  const logout = () => {
    localStorage.removeItem('clientToken');
    setClientToken('');

    localStorage.removeItem('client');
    setClient(null);

    navigate(routes.root);
  };

  return (
    <ClientContext.Provider
      value={{
        client,
        clientToken,
        login,
        logout,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within ClientProvider');
  }
  return context;
};
